import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucidePuzzle } from '@ng-icons/lucide';
import type { ProviderCatalogEntry } from '../domain/provider-catalog-entry';

type ProviderBrandIconVariant = 'color' | 'mono';

@Component({
  selector: 'app-provider-brand-icon',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, NgOptimizedImage],
  providers: [provideIcons({ lucidePuzzle })],
  template: `
    <span
      class="brand"
      [class.brand--fallback]="showFallback()"
      [class.brand--mono]="variant() === 'mono'"
      [style.--brand-color]="brandColor()"
      [style.width.rem]="sizeRem()"
      [style.height.rem]="sizeRem()"
      aria-hidden="true"
    >
      @if (!showFallback() && logoSrc(); as src) {
        <img
          class="brand__logo"
          [ngSrc]="src"
          [width]="imageSize()"
          [height]="imageSize()"
          alt=""
          (error)="onLoadError()"
        />
      } @else {
        <ng-icon class="brand__fallback-icon" name="lucidePuzzle" [size]="fallbackIconSize()" />
      }
    </span>
  `,
  styles: [
    `
      .brand {
        --brand-color: #6b7280;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border-radius: var(--radius-sm);
        border: 1px solid color-mix(in oklch, var(--brand-color) 20%, var(--color-line));
        background: color-mix(in oklch, var(--brand-color) 12%, var(--color-panel));
        overflow: hidden;
      }

      .brand--mono {
        border-color: color-mix(in oklch, var(--color-muted) 22%, var(--color-line));
        background: color-mix(in oklch, var(--color-soft) 72%, transparent);
      }

      .brand--fallback {
        background: var(--color-soft);
      }

      .brand__logo {
        display: block;
        width: 68%;
        height: 68%;
        object-fit: contain;
      }

      .brand__fallback-icon {
        color: var(--color-muted);
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
    `,
  ],
})
export class ProviderBrandIconComponent {
  readonly entry = input<ProviderCatalogEntry | null>(null);
  readonly size = input<number>(2.4);
  readonly variant = input<ProviderBrandIconVariant>('color');

  readonly sizeRem = computed(() => this.size());
  readonly imageSize = computed(() => Math.max(16, Math.round(this.size() * 16)));
  readonly fallbackIconSize = computed(() => `${Math.max(14, Math.round(this.size() * 12))}px`);
  readonly brandColor = computed(() => this.entry()?.brandColor ?? '#6B7280');
  readonly logoSrc = computed(() => {
    const entry = this.entry();
    if (!entry) return null;
    return this.variant() === 'mono' ? entry.logoMonoPath : entry.logoColorPath;
  });

  /** Marca quando o <img> 404 (provider sem arquivo de logo) — cai pro ícone padrão. */
  protected readonly loadFailed = signal(false);
  protected readonly showFallback = computed(() => !this.logoSrc() || this.loadFailed());

  constructor() {
    // Reseta a flag quando o src muda (outro entry/variant).
    effect(() => {
      this.logoSrc();
      this.loadFailed.set(false);
    });
  }

  protected onLoadError(): void {
    this.loadFailed.set(true);
  }
}
